import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import {
  DEFAULT_PUBLIC_SITE_PROGRESS_STEPS,
  type PublicSiteProgressStep,
  type StepTrackerStatusMode,
} from '../lib/progressContent';

interface ProgressIndicatorProps {
  steps?: PublicSiteProgressStep[];
  placement?: 'fixed' | 'static' | 'sticky';
  activeStepId?: string | null;
  statusMode?: StepTrackerStatusMode;
  stickySectionIds?: string[];
  forceVisible?: boolean;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps = DEFAULT_PUBLIC_SITE_PROGRESS_STEPS,
  placement = 'fixed',
  activeStepId = null,
  statusMode = 'current',
  stickySectionIds = [],
  forceVisible = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visibleInStickySection, setVisibleInStickySection] = useState(true);
  const [portalReady, setPortalReady] = useState(false);
  const normalizedSteps = useMemo(
    () => steps.filter((step) => step.id.trim() && step.label.trim()),
    [steps]
  );
  const normalizedStickySectionIds = useMemo(
    () => stickySectionIds.map((id) => id.trim()).filter(Boolean),
    [stickySectionIds]
  );
  const activeStepIndex = useMemo(() => {
    if (!activeStepId) return -1;
    return normalizedSteps.findIndex((step) => step.id === activeStepId);
  }, [activeStepId, normalizedSteps]);

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    if (activeStepIndex >= 0) {
      setCurrentStep(activeStepIndex);
      return undefined;
    }

    if (!normalizedSteps.length) {
      setCurrentStep(0);
      return undefined;
    }

    const handleScroll = () => {
      const sections = normalizedSteps.map((step, index) => {
        const element = document.querySelector(`[data-step="${step.id}"]`);
        if (!element) return null;

        const rect = element.getBoundingClientRect();
        const isInView = rect.top <= window.innerHeight / 2 && rect.bottom >= window.innerHeight / 2;

        return { step: index, isInView };
      }).filter(Boolean);

      const inViewSection = sections.find(s => s?.isInView);
      if (inViewSection) {
        setCurrentStep(inViewSection.step);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeStepIndex, normalizedSteps]);

  useEffect(() => {
    if (forceVisible || normalizedStickySectionIds.length === 0) {
      setVisibleInStickySection(true);
      return undefined;
    }

    setVisibleInStickySection(false);

    const handleScroll = () => {
      const probeY = Math.min(Math.max(window.innerHeight * 0.28, 96), window.innerHeight / 2);
      const isVisible = normalizedStickySectionIds.some((sectionId) => {
        const element =
          document.getElementById(sectionId) ??
          document.querySelector(`[data-section-id="${CSS.escape(sectionId)}"]`);
        if (!element) return false;
        const rect = element.getBoundingClientRect();
        return rect.top <= probeY && rect.bottom >= probeY;
      });
      setVisibleInStickySection(isVisible);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [forceVisible, normalizedStickySectionIds]);

  if (!normalizedSteps.length || !visibleInStickySection) {
    return null;
  }

  const placementClass =
    placement === 'fixed'
      ? 'fixed left-0 right-0 top-0 z-[240]'
      : placement === 'sticky'
        ? 'sticky top-0 z-[240]'
        : 'relative z-[1]';

  const tracker = (
    <div
      className={`bg-white/95 backdrop-blur-sm border-b border-stone-200 shadow-sm ${placementClass}`}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          {normalizedSteps.map((step, index) => {
            const isCompleted = statusMode === 'completed' ? index <= currentStep : index < currentStep;
            const nextCurrentStep =
              statusMode === 'completed' ? Math.min(currentStep + 1, normalizedSteps.length - 1) : currentStep;
            const isCurrent = !isCompleted && index === nextCurrentStep;

            return (
              <React.Fragment key={step.id}>
                {/* Step */}
                <div className="flex items-center gap-2 flex-1">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-all ${
                      isCompleted
                        ? 'bg-green-600 text-white'
                        : isCurrent
                        ? 'bg-stone-800 text-white ring-4 ring-stone-200'
                        : 'bg-stone-200 text-stone-500'
                    }`}
                  >
                    {isCompleted ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  <span
                    className={`text-sm font-medium hidden sm:inline ${
                      isCurrent ? 'text-stone-900' : isCompleted ? 'text-green-600' : 'text-stone-400'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>

                {/* Connector */}
                {index < normalizedSteps.length - 1 && (
                  <div
                    className={`h-0.5 w-full max-w-[60px] transition-all ${
                      isCompleted ? 'bg-green-600' : 'bg-stone-200'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );

  if (placement === 'fixed' && portalReady && typeof document !== 'undefined') {
    return createPortal(tracker, document.body);
  }

  return tracker;
};

export default ProgressIndicator;
